import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tag, Plus, Search, Edit, Trash2, ToggleLeft, ToggleRight, RefreshCcw, Eye } from 'lucide-react';
import alertService from '../services/alertService';
import productService from '../services/productService';
import { PageResponse } from '../types/api';
import { ProductPriceResponseDTO } from '../types/product';
import PriceModal from '../components/prices/PriceModal';
import { SearchableSelect } from '../components/ui/SearchableSelect';

const PricesPage: React.FC = () => {
  const [items, setItems] = useState<ProductPriceResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ProductPriceResponseDTO | null>(null);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [filterProdutoId, setFilterProdutoId] = useState<string>('');
  const [onlyActive, setOnlyActive] = useState<boolean>(false);

  const [productOptions, setProductOptions] = useState<Array<{ id: string; nome?: string; descricao?: string }>>([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const productSearchTimeoutRef = useRef<number | null>(null);

  const onSearchProducts = (term: string) => {
    // Debounce de 300ms
    if (productSearchTimeoutRef.current) {
      window.clearTimeout(productSearchTimeoutRef.current);
    }
    const timeoutId = window.setTimeout(async () => {
      const s = (term || '').trim();
      if (!s) { setProductOptions([]); return; }
      try {
        setProductSearchLoading(true);
        const res = await productService.searchByTerm(s, { page: 0, size: 10 });
        const opts = (res.data?.content || []).map((p) => ({ id: p.id, nome: p.nome, descricao: p.codigo }));
        setProductOptions(opts);
      } finally {
        setProductSearchLoading(false);
      }
    }, 300);
    productSearchTimeoutRef.current = timeoutId;
  };

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const byProduct = filterProdutoId ? it.produtoId === filterProdutoId : true;
      const byActive = onlyActive ? !!it.ativo : true;
      return byProduct && byActive;
    });
  }, [items, filterProdutoId, onlyActive]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await productService.listPrices({ page, size, produtoId: filterProdutoId || undefined });
      const pageData = res.data as PageResponse<ProductPriceResponseDTO>;
      setItems(pageData?.content || []);
      setTotalPages(pageData?.totalPages || 0);
    } catch (error) {
      alertService.error('Erro ao carregar preços');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, size]);

  const handleCreate = () => { setEditing(null); setShowModal(true); };
  const handleEdit = (item: ProductPriceResponseDTO) => { setEditing(item); setShowModal(true); };
  
  const handleClearFilters = () => { 
    setFilterProdutoId(''); 
    setOnlyActive(false);
    setPage(0);
    load();
  };

  const handleDelete = async (item: ProductPriceResponseDTO) => {
    const confirm = await alertService.confirm('Excluir preço?', `Esta ação não pode ser desfeita. Produto: ${item.produtoNome || item.produtoId}`);
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Excluindo...');
      await productService.deletePrice(item.id);
      alertService.close();
      alertService.success('Preço excluído');
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao excluir preço');
    }
  };

  const handleToggleActive = async (item: ProductPriceResponseDTO) => {
    try {
      alertService.loading(`${item.ativo ? 'Desativando' : 'Ativando'}...`);
      const res = item.ativo ? await productService.deactivatePrice(item.id) : await productService.activatePrice(item.id);
      alertService.close();
      setItems((prev) => prev.map((x) => (x.id === item.id ? (res.data || x) : x)));
      alertService.success(`Preço ${item.ativo ? 'desativado' : 'ativado'}`);
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao alterar status');
    }
  };

  const handleSaved = (saved: ProductPriceResponseDTO) => {
    setItems((prev) => {
      const exists = prev.some((x) => x.id === saved.id);
      return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev];
    });
  };

  const formatCurrency = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString || '-';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag className="h-6 w-6 text-red-600" />
          <h1 className="text-xl font-semibold text-gray-900">Gerenciador de Preços</h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg" onClick={load} disabled={loading}>
            <RefreshCcw className="h-4 w-4" />
            Recarregar
          </button>
          <button className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            Novo Preço
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <SearchableSelect
                options={productOptions}
                value={filterProdutoId}
                onChange={(v) => { setFilterProdutoId(String(v)); setPage(0); }}
                label="Filtrar por Produto"
                placeholder="Buscar produto"
                onSearch={onSearchProducts}
                loading={productSearchLoading}
              />
            </div>
            <label className="inline-flex items-center gap-2 px-2 py-2">
              <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
              Mostrar apenas ativos
            </label>
            <div className="flex items-end gap-2">
              <button type="button" onClick={() => { setPage(0); load(); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Buscar</button>
              <button type="button" onClick={handleClearFilters} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Limpar Filtros</button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venda</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Custo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr className="hover:bg-gray-50"><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Carregando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr className="hover:bg-gray-50"><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Nenhum preço encontrado</td></tr>
              ) : (
                filtered.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap px-4 py-2 text-gray-700">{item.produtoCodigo ? `${item.produtoCodigo} - ` : ''}{item.produtoNome || item.produtoId}</td>
                    <td className="px-6 py-4 whitespace-nowrappx-4 py-2 font-medium text-gray-900">{formatCurrency(item.precoVenda)}</td>
                    <td className="px-6 py-4 whitespace-nowrap px-4 py-2 text-gray-700">{formatCurrency(item.precoCusto)}</td>
                    <td className="px-6 py-4 whitespace-nowrap px-4 py-2 text-gray-700">
                      {formatDate(item.dataInicio)}{item.dataFim ? ` até ${formatDate(item.dataFim)}` : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${item.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {item.ativo ? 'ATIVO' : 'INATIVO'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap px-4 py-2">
                      <div className="flex items-center gap-2">
                        
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-red-600 hover:text-red-700 p-1 rounded"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleToggleActive(item)}
                          className={`p-1 rounded ${
                            item.ativo
                              ? 'text-orange-600 hover:text-orange-700'
                              : 'text-green-600 hover:text-green-700'
                          }`}
                          title={item.ativo ? 'Desativar' : 'Ativar'}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        
                        {/* <button className="inline-flex items-center gap-1 px-2 py-1 border rounded hover:bg-gray-50 text-red-600" onClick={() => handleDelete(item)} title="Excluir">
                          <Trash2 className="h-4 w-4" />
                          Excluir
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      <div className="flex items-center gap-2 justify-end mt-3">
        <button disabled={page <= 0} onClick={() => setPage((p) => Math.max(p - 1, 0))} className="px-3 py-1 border rounded disabled:opacity-50">Anterior</button>
        <span className="text-sm text-gray-600">Página {page + 1} de {totalPages}</span>
        <button disabled={(page + 1) >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Próxima</button>
        <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="px-2 py-1 border rounded">
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>

      {showModal && (
        <PriceModal
          price={editing || undefined}
          onClose={() => setShowModal(false)}
          onSave={handleSaved}
        />
      )}
    </div>
  );
};

export default PricesPage;